import { apiv1 as api } from '@/adapters';
import type { SecretTemplateModel } from '@/adapters/apiv1.g';
import {
  extractAccepted,
  extractBoolean,
  extractSecret,
  extractSecretTemplate,
  unwrapData,
} from '@/adapters/shared';
import type { Secret } from '@/types/core/secret';
import type { SecretTemplate } from '@/types/core/secret-template';
import type { JSONObject } from '@/types/shared/json-schema';
import { type UUID, newUUID } from '@/utils/uuid';

export const secretService = {
  /** List all active secrets in tenant */
  listSecrets: async (tenantId: UUID): Promise<Secret[]> => {
    const res = await api.listSecrets(tenantId);
    const data = unwrapData(res);
    if (!data || !Array.isArray(data)) return [];
    return data.map(extractSecret);
  },

  /** Find secret metadata by ID */
  findSecret: async (
    tenantId: UUID,
    secretId: UUID
  ): Promise<Secret | null> => {
    const res = await api.findSecret(tenantId, secretId);
    const data = unwrapData(res);
    if (!data) return null;
    return extractSecret(data);
  },

  /** Create a new encrypted secret (type is defined by the chosen template) */
  createSecret: async (
    tenantId: UUID,
    body: {
      name: string;
      raw: JSONObject;
      templateId: string;
    }
  ): Promise<Secret> => {
    const secretId = newUUID();
    const res = await api.createSecret(tenantId, {
      secret_id: secretId,
      name: body.name,
      raw: body.raw,
      template_id: body.templateId,
    });
    if (!extractAccepted(res)) {
      throw new Error('Failed to create secret');
    }
    // Return minimal secret object until API provides full details
    return {
      secretId: secretId,
      name: body.name,
      templateId: body.templateId as UUID,
      status: 'active',
      tmkVersion: 0,
      algVersion: 0,
      isOutdated: false,
      activeTmkVersion: 0,
      activeAlgVersion: 0,
    };
  },

  /** Update secret name */
  updateSecretName: async (
    tenantId: UUID,
    secretId: UUID,
    name: string
  ): Promise<boolean> => {
    const res = await api.updateSecretName(tenantId, secretId, { name });
    return extractAccepted(res);
  },

  /** Update secret value */
  updateSecretValue: async (
    tenantId: UUID,
    secretId: UUID,
    raw: JSONObject
  ): Promise<boolean> => {
    const res = await api.updateSecretValue(tenantId, secretId, {
      raw,
    });
    return extractAccepted(res);
  },

  /** Remove a secret */
  deleteSecret: async (tenantId: UUID, secretId: UUID): Promise<boolean> => {
    const res = await api.removeSecret(tenantId, secretId);
    return extractBoolean(res);
  },

  /** Access and decrypt secret value (audited) */
  // oxlint-disable-next-line typescript/no-explicit-any
  accessSecret: async (tenantId: UUID, secretId: UUID): Promise<any> => {
    const res = await api.accessSecret(tenantId, secretId);
    return unwrapData(res);
  },

  /** List secret templates (paged). API returns { models, next }. */
  listSecretTemplates: async (
    tenantId: UUID
  ): Promise<{ models: SecretTemplate[]; next: string }> => {
    const res = await api.listSecretTemplates(tenantId);
    const raw = unwrapData(res) as
      | { models?: SecretTemplateModel[]; next?: string }
      | SecretTemplateModel[]
      | null
      | undefined;
    if (!raw) return { models: [], next: '' };
    const models = Array.isArray(raw)
      ? raw
      : Array.isArray(raw.models)
        ? raw.models
        : [];
    const next =
      !Array.isArray(raw) && typeof raw.next === 'string' ? raw.next : '';
    return {
      models: models.map(extractSecretTemplate),
      next,
    };
  },

  /** Find a specific secret template by ID */
  findSecretTemplate: async (
    tenantId: UUID,
    templateId: UUID
  ): Promise<SecretTemplate | null> => {
    const res = await api.findSecretTemplate(tenantId, templateId);
    const data = unwrapData<SecretTemplateModel>(res);
    if (!data) return null;
    return extractSecretTemplate(data);
  },

  /** Delete a secret template */
  deleteSecretTemplate: async (
    tenantId: UUID,
    templateId: UUID
  ): Promise<boolean> => {
    const res = await api.removeSecretTemplate(tenantId, templateId);
    return extractBoolean(res);
  },

  /** Create or update a secret template (reconcile) */
  reconcileSecretTemplate: async (
    tenantId: UUID,
    body: {
      name: string;
      description?: string;
      schema: object;
      existingTemplateId?: UUID;
    }
  ): Promise<SecretTemplate> => {
    const templateId = body.existingTemplateId ?? newUUID();
    const res = await api.reconcileSecretTemplate(tenantId, templateId, {
      name: body.name,
      description: body.description ?? '',
      schema: JSON.stringify(body.schema),
    });
    if (!extractAccepted(res)) {
      throw new Error('Failed to create secret template');
    }
    return {
      templateId: templateId as UUID,
      name: body.name,
      description: body.description,
      schema: body.schema as SecretTemplate['schema'],
    };
  },
};
